import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NotificationService } from '../../notification/notification.service';
import { AuthService } from '../../core/security/auth.service';
import { ContactService } from '../contact.service';
import { ContactModel } from '../../api/models/contact-model';
import { RolesEnum } from '../../core/security/roles.enum';
import { ModulesEnum } from '../../core/security/modules.enum';
import { MatDialog } from '@angular/material/dialog';
import { DuplicatesWarningDialogComponent } from './duplicates-warning-dialog.component';
import { TagsGridDialogComponent } from '../../tags/tags-list-dialog.component';
import { phoneNumberValidator } from '../../core/validators/phone-number.validator';
import { MatConfirmDialogComponent } from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { CurrentUser } from '../../api/models/current-user';
import { CustomFieldModel } from '../../api/models/custom-field-model';
import { CustomFieldValueModel } from '../../api/models/custom-field-value-model';
import { FormHelpers } from '../../core/utilities/form-helpers';
import { NgIf, NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FlexModule } from '@angular/flex-layout';
import { MatSimpleSelectComponent } from '../../editors/mat-simple-select/mat-simple-select.component';
import { MatSlideToggleInputComponent } from '../../editors/mat-slide-toggle-input/mat-slide-toggle-input.component';
import { MatTagsMultiselectComponent } from '../../editors/mat-tags-multiselect/mat-tags-multiselect.component';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatPhoneNumberInputComponent } from '../../editors/mat-phone-number-input/mat-phone-number-input.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatNumberInputComponent } from '../../editors/mat-number-input/mat-number-input.component';
import { MatDatetimeInputComponent } from '../../editors/mat-datetime-input/mat-datetime-input.component';
import { MatMultiselectComponent } from '../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatButton } from '@angular/material/button';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatIcon } from '@angular/material/icon';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';

@Component({
  selector: 'app-contact-edit',
  templateUrl: './contact-edit.component.html',
  styleUrls: ['./contact-edit.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatCard,
    MatCardContent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCardTitle,
    FlexModule,
    MatSimpleSelectComponent,
    MatSlideToggleInputComponent,
    MatTagsMultiselectComponent,
    MatTextInputComponent,
    MatPhoneNumberInputComponent,
    MatCheckboxInputComponent,
    MatTooltip,
    NgFor,
    NgSwitch,
    NgSwitchCase,
    MatNumberInputComponent,
    MatDatetimeInputComponent,
    MatMultiselectComponent,
    MatButton,
    Angular2PromiseButtonModule,
    MatIcon,
  ],
})
export class ContactEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isModal = false;
  @Input() contactId: string;
  @Output() onSaveOrDelete = new EventEmitter<boolean>();

  phoneNumberDisplayPipe = new PhoneNumberPipe();

  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  canEditTags = false;
  hasWhatsAppModule = false;
  editModel: ContactModel = { id: 'new', smsPhoneType: 'mobileNumber', communicationType: 0, recordType: 0 };

  optedInTooltip = 'Client opted in to use WhatsApp';
  notOptedInTooltip =
    'You cannot send WhatsApp message, because contact is not opted in. Send welcome text message, requesting to reply with code: "whatsapp"';

  isLoading = false;

  buttonSubmitPromise: Promise<void>;
  public id: string;
  private paramsSubscription;
  private queryParamsSubscription;
  hasTagsViewAccess = false;
  customFieldsDefinitions?: Array<CustomFieldModel>;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public contactService: ContactService,
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.canEditTags =
      this.authService.hasAccess([RolesEnum.Tags_Edit, RolesEnum.Tags_Delete]) &&
      this.authService.userHasPackage(ModulesEnum.TagsModule);

    this.editRoles = [];
    this.deleteRoles = [];

    this.hasTagsViewAccess =
      this.authService.hasAccess([RolesEnum.Tags_View, RolesEnum.Tags_Edit, RolesEnum.Tags_Delete]) &&
      this.authService.userHasPackage(ModulesEnum.TagsModule);

    if (!this.isModal) {
      this.headerService.showBackButton(true);
    }

    this.createForm();
  }

  createForm(): void {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      phoneNumber: ['', [phoneNumberValidator]],
      mobileNumber: ['', [phoneNumberValidator]],
      faxNumber: ['', [phoneNumberValidator]],
      email: ['', [Validators.email]],
      companyName: ['', []],
      position: ['', []],
      speedDial: ['', [Validators.pattern(/^\*?\d+$/)]],
      smsPhoneType: ['', []],
      communicationType: [0, []],
      recordType: [0, [Validators.required]],
      syncPbx: [false, []],
      blacklisted: [false, []],
      tags: [[], []],
    });
  }

  createCustomFieldsFormGroup(customFields: CustomFieldModel[]): void {
    let group: any = {};

    customFields.forEach(field => {
      if (field.required) {
        group[field.fieldKey] = [null, Validators.required];
      } else {
        group[field.fieldKey] = [null];
      }
    });

    this.form.addControl('customFields', this.fb.group(group));
  }

  async onSubmit() {
    this.serverError = false;
    FormHelpers.forceValidate(this.form);
    if (this.form.valid && this.canEdit()) {
      // check for duplicates
      const reqData = this.prepareFormModel();

      const phoneNumbers = [reqData.phoneNumber || '', reqData.mobileNumber || '', reqData.faxNumber || ''].filter(
        x => !!x,
      );

      const duplicates = await this.contactService.queryContacts({
        phoneNumbers: phoneNumbers,
        firstName: phoneNumbers.length == 0 ? reqData.firstName : undefined,
        lastName: phoneNumbers.length == 0 ? reqData.lastName : undefined,
        contactType: reqData.recordType?.toString(),
        excludeId: reqData.id === 'new' ? null : reqData.id,
        limit: 100,
      });

      if (duplicates.total > 0) {
        const dialogRef = this.dialog.open(DuplicatesWarningDialogComponent, {
          height: '90%',
          data: {
            records: duplicates.data,
          },
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result === 'proceed') {
            this.submitted = false;
            this.buttonSubmitPromise = this.processSubmit();
          } else {
            this.submitted = false;
          }
        });
      } else {
        this.buttonSubmitPromise = this.processSubmit();
      }
    } else {
      this.submitted = false;
    }
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    this.hasWhatsAppModule = user?.permissions.hasWhatsAppModule || false;
  }

  async processSubmit(): Promise<void> {
    try {
      const reqData = this.prepareFormModel();

      this.submitted = true;
      await this.contactService.saveContact(reqData);
      this.notificationService.notifySuccess('Success');

      if (this.isModal) {
        this.onSaveOrDelete?.emit(true);
      } else {
        this.headerService.goBack();
      }
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): ContactModel {
    const formModel = Object.assign({}, this.form.value);

    const customFields: CustomFieldValueModel[] = this.customFieldsDefinitions?.map(d => {
      const entityFieldValue = formModel.customFields[d.fieldKey] ?? null;
      return {
        fieldKey: d.fieldKey,
        textValue: d.fieldType == 'text' ? (entityFieldValue != null ? `${entityFieldValue}` : null) : null,
        boolValue: d.fieldType == 'boolean' ? (entityFieldValue ?? false) : null,
        numberValue: d.fieldType == 'number' ? (entityFieldValue ?? 0) : null,
        dateValue: d.fieldType == 'date' && entityFieldValue ? (new Date(entityFieldValue).toISOString() ?? '') : null,
        listValue: d.fieldType == 'list' ? (entityFieldValue ?? null) : null,
      };
    });

    return {
      id: this.editModel.id,
      firstName: formModel.firstName,
      lastName: formModel.lastName,
      phoneNumber: formModel.phoneNumber,
      mobileNumber: formModel.mobileNumber,
      faxNumber: formModel.faxNumber,
      companyName: formModel.companyName,
      position: formModel.position,
      speedDial: formModel.speedDial,
      email: formModel.email,
      recordType: formModel.recordType,
      smsPhoneType: formModel.smsPhoneType,
      communicationType: formModel.communicationType,
      syncPbx: formModel.syncPbx ? 1 : 0,
      blacklisted: formModel.blacklisted ? 1 : 0,
      tags: formModel.tags ? formModel.tags : [],
      customFields: customFields,
    };
  }

  ngOnInit() {
    super.ngOnInit();
    this.isLoading = true;
    this.initAsyncData().then(() => {
      this.ngOnChanges();
      this.isLoading = false;
    });
  }

  async initAsyncData() {
    if (!this.isModal) {
      this.paramsSubscription = this.route.params.subscribe(params => {
        this.id = params['id'];
        this.onUrlChanged(params);
      });

      this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
        this.onUrlChanged(params);
      });
    } else {
      this.id = this.contactId;
    }

    if (this.id !== 'new') {
      this.editModel = await this.contactService.getContactById(this.id);
    }

    if (this.canEditTags) {
      await this.contactService.getTags();
    }

    this.customFieldsDefinitions = await this.contactService.getCustomFieldsDefinitions();

    if (this.customFieldsDefinitions?.length > 0) {
      this.createCustomFieldsFormGroup(this.customFieldsDefinitions);
    } else {
      this.form.removeControl('customFields');
    }
  }

  onUrlChanged(params: Params) {
    if (this.id === 'new') {
      const faxNumber = params['faxNumber'];
      const phoneNumber = params['phoneNumber'];
      console.log('new url param: ' + faxNumber);
      console.log('Router params', this.id);
      if (faxNumber) {
        this.editModel.faxNumber = faxNumber;
        this.ngOnChanges();
      }
      if (phoneNumber) {
        this.editModel.phoneNumber = phoneNumber;
        this.ngOnChanges();
      }
    }
  }

  ngOnChanges(changes?: SimpleChanges): void {
    console.log('ngOnChanges');

    const customFields = {};
    this.customFieldsDefinitions?.forEach(d => {
      const entityField = this.editModel.customFields?.find(x => x.fieldKey === d.fieldKey);
      if (d.fieldType == 'number' && entityField && entityField.numberValue) {
        customFields[d.fieldKey] = entityField.numberValue;
      } else if (d.fieldType == 'boolean' && entityField) {
        customFields[d.fieldKey] = entityField.boolValue ?? false;
      } else if (d.fieldType == 'date' && entityField) {
        customFields[d.fieldKey] = entityField.dateValue ?? '';
      } else if (d.fieldType == 'list' && entityField) {
        customFields[d.fieldKey] = entityField.listValue ?? [];
      } else {
        customFields[d.fieldKey] = entityField ? entityField.textValue : null;
      }
    });

    this.form.reset({
      firstName: this.editModel.firstName,
      lastName: this.editModel.lastName,
      phoneNumber: this.editModel.phoneNumber,
      mobileNumber: this.editModel.mobileNumber,
      faxNumber: this.editModel.faxNumber,
      companyName: this.editModel.companyName,
      position: this.editModel.position,
      speedDial: this.editModel.speedDial,
      email: this.editModel.email,
      recordType: this.editModel.recordType,
      smsPhoneType: this.editModel.smsPhoneType,
      communicationType: this.editModel.communicationType,
      syncPbx: this.editModel.syncPbx === 1,
      blacklisted: this.editModel.blacklisted === 1,
      tags: this.editModel.tags || [],
      customFields: customFields,
    });
  }

  canEdit(): boolean {
    if (this.editModel.id === 'new') {
      return true;
    }
    if (this.editModel.recordType === 0 && this.contactService.hasPublicContactsEditAccess) {
      return true;
    }
    if (this.editModel.recordType === 1 && this.contactService.hasPrivateContactsEditAccess) {
      return true;
    }
    return false;
  }

  canDelete(): boolean {
    if (this.editModel.id === 'new') {
      return false;
    }
    if (this.editModel.recordType === 0 && this.contactService.hasPublicContactsDeleteAccess) {
      return true;
    }
    if (this.editModel.recordType === 1 && this.contactService.hasPrivateContactsDeleteAccess) {
      return true;
    }
    return false;
  }

  onManageTagsClick($event: MouseEvent) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(TagsGridDialogComponent, {
      width: '600px',
      height: '90%',
    });

    dialogRef.afterClosed().subscribe(async () => {
      await this.contactService.getTags();
    });
  }

  onDeleteClicked() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      // height: '90%',
      data: {
        title: 'Delete Contact?',
        actionTitle: 'Delete',
        text: 'Are you sure you want to delete Contact?',
        hideCancel: false,
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.contactService.deleteContact(this.editModel.id);
        await this.notificationService.notifySuccess('Contact has been deleted');

        if (this.isModal) {
          this.onSaveOrDelete?.emit(true);
        } else {
          this.headerService.goBack();
        }

        console.log('end delete');
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();

    if (!this.isModal) {
      this.headerService.showBackButton(false);
    }
  }
}
